import { formatForDisplay, useHotkey } from "@tanstack/react-hotkeys";
import { ClipboardPaste, Folder, FolderPlus, Upload } from "lucide-react";
import { useRef, useState } from "react";

import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuShortcut,
	ContextMenuTrigger,
} from "@lawline/ui/components/context-menu";
import { cn } from "@lawline/ui/core/cn";

import { FileBrowserItem } from "./file-browser-item";
import { FileBrowserToolbar } from "./file-browser-toolbar";
import type {
	FileBrowserItem as Item,
	FileBrowserProps,
} from "./file-browser.types";
import { NameInput } from "./name-input";

const PASTE_HINT = formatForDisplay("Mod+V");

export function FileBrowser({
	items,
	path = [],
	onOpenFolder,
	onCreateFolder,
	onRename,
	onMove,
	onReorder,
	onPaste,
	onDelete,
	onUpload,
	onNavigate,
	onBack,
	onForward,
	canBack = false,
	canForward = false,
	loading = false,
	className,
}: FileBrowserProps) {
	const [renamingId, setRenamingId] = useState<string | null>(null);
	const [creating, setCreating] = useState(false);
	const [clipboard, setClipboard] = useState<Item | null>(null);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [draggingId, setDraggingId] = useState<string | null>(null);
	const fileRef = useRef<HTMLInputElement>(null);

	const triggerUpload = () => fileRef.current?.click();
	const resolve = (id: string) => items.find((i) => i.id === id);
	const paste = () => {
		if (!clipboard) return;
		onPaste?.(clipboard);
		setClipboard(null);
	};

	// Cmd/Ctrl+X cuts the selected item, Cmd/Ctrl+V pastes. ignoreInputs keeps
	// native text cut/paste working while renaming.
	useHotkey(
		"Mod+X",
		() => {
			const sel = selectedId && resolve(selectedId);
			if (sel) setClipboard(sel);
		},
		{ ignoreInputs: true },
	);
	useHotkey("Mod+V", paste, { ignoreInputs: true });

	return (
		<div className={cn("bg-background flex flex-col rounded-lg border", className)}>
			<FileBrowserToolbar
				path={path}
				onNavigate={(index) => onNavigate?.(index)}
				onBack={() => onBack?.()}
				onForward={() => onForward?.()}
				canBack={canBack}
				canForward={canForward}
				onNewFolder={() => setCreating(true)}
				onUpload={triggerUpload}
				loading={loading}
			/>
			<ContextMenu>
				<ContextMenuTrigger asChild>
					<div
						className="flex min-h-96 flex-wrap content-start gap-1 p-3"
						onDragOver={(e) => e.preventDefault()}
						onDrop={(e) => {
							e.preventDefault();
							const source = resolve(e.dataTransfer.getData("text/plain"));
							if (source) onReorder?.(source, null);
						}}
					>
						{items.map((item) => (
							<FileBrowserItem
								key={item.id}
								item={item}
								renaming={renamingId === item.id}
								onOpen={() => onOpenFolder?.(item)}
								onStartRename={() => setRenamingId(item.id)}
								onCancelRename={() => setRenamingId(null)}
								onRename={(name) => {
									onRename?.(item, name);
									setRenamingId(null);
								}}
								onMoveInto={(sourceId) => {
									const source = resolve(sourceId);
									if (source) onMove?.(source, item);
								}}
								onReorderBefore={(sourceId) => {
									const source = resolve(sourceId);
									if (source) onReorder?.(source, item.id);
								}}
								onCut={() => setClipboard(item)}
								cut={clipboard?.id === item.id}
								selected={selectedId === item.id}
								onSelect={() => setSelectedId(item.id)}
								dragging={draggingId === item.id}
								onDragStart={() => setDraggingId(item.id)}
								onDragEnd={() => setDraggingId(null)}
								onDelete={() => onDelete?.(item)}
							/>
						))}
						{creating && (
							<div className="flex w-24 flex-col items-center gap-1 p-2">
								<Folder
									className="text-muted-foreground size-12"
									strokeWidth={1}
								/>
								<NameInput
									defaultValue="untitled folder"
									onCommit={(name) => {
										onCreateFolder?.(name);
										setCreating(false);
									}}
									onCancel={() => setCreating(false)}
								/>
							</div>
						)}
					</div>
				</ContextMenuTrigger>
				<ContextMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
					<ContextMenuItem onSelect={() => setCreating(true)}>
						<FolderPlus className="size-4" />
						New Folder
					</ContextMenuItem>
					<ContextMenuItem onSelect={triggerUpload}>
						<Upload className="size-4" />
						Upload
					</ContextMenuItem>
					<ContextMenuItem disabled={!clipboard} onSelect={paste}>
						<ClipboardPaste className="size-4" />
						Paste
						<ContextMenuShortcut>{PASTE_HINT}</ContextMenuShortcut>
					</ContextMenuItem>
				</ContextMenuContent>
			</ContextMenu>
			<input
				ref={fileRef}
				type="file"
				multiple
				hidden
				onChange={(e) => {
					if (e.target.files?.length) onUpload?.(e.target.files);
					e.target.value = "";
				}}
			/>
		</div>
	);
}
